'use client';

import { useLayoutEffect, useSyncExternalStore } from 'react';
import { IDLE_AUTH_HANDOFF, type AuthHandoffState } from './auth-handoff-phase';

/**
 * Sign-in → app handoff store (#5559). One veil, mounted in the root layout,
 * covers the hop from the landing-style auth pages into the app; this module
 * is what sign-in code and loaders tell it. See `auth-handoff-phase.ts` for
 * when it covers and `auth-handoff-veil.tsx` for the surface.
 */

/** `<html>` attribute that fades and scales the auth card out (auth-handoff.css). */
export const AUTH_HANDOFF_LEAVING_ATTRIBUTE = 'data-auth-handoff-leaving';

let state: AuthHandoffState = IDLE_AUTH_HANDOFF;
const listeners = new Set<() => void>();

function update(next: Partial<AuthHandoffState>) {
  state = { ...state, ...next };
  for (const listener of listeners) listener();
}

function setLeaving(leaving: boolean) {
  if (typeof document === 'undefined') return;
  document.documentElement.toggleAttribute(
    AUTH_HANDOFF_LEAVING_ATTRIBUTE,
    leaving
  );
}

/** A new handoff (or the end of one): holds from an older one stop counting. */
function startGeneration(next: Partial<AuthHandoffState>) {
  update({ ...next, holds: 0, generation: state.generation + 1 });
}

/**
 * A sign-in succeeded and the page is about to navigate to
 * `/auth/signing-in`: start covering now, in the same tick as the push.
 */
export function beginAuthHandoff() {
  setLeaving(true);
  startGeneration({ active: true, reachedSigningIn: false, yielded: false });
}

/** `/auth/signing-in` rendered (soft navigation or a document load). */
export function markAuthHandoffAtSigningIn() {
  setLeaving(false);
  if (state.active && state.reachedSigningIn) return;
  // A soft sign-in keeps its generation: loaders it already counts stay.
  if (state.active) update({ reachedSigningIn: true });
  else
    startGeneration({ active: true, reachedSigningIn: true, yielded: false });
}

/**
 * Give the screen back: the veil lifts through its normal reveal on any
 * route, `/auth/signing-in` included. Idempotent.
 */
export function yieldAuthHandoff() {
  setLeaving(false);
  if (!state.active || state.yielded) return;
  update({ yielded: true });
}

export function endAuthHandoff() {
  setLeaving(false);
  if (!state.active && state.holds === 0) return;
  // `yielded` survives the end: /auth/signing-in keeps its recovery up.
  startGeneration({ active: false, reachedSigningIn: false });
}

/**
 * Counts a mounted loader in the current handoff; the returned function
 * releases it. A release from an older handoff changes nothing.
 */
export function addAuthHandoffHold(): () => void {
  const generation = state.generation;
  update({ holds: state.holds + 1 });
  let released = false;
  return () => {
    if (released) return;
    released = true;
    if (generation !== state.generation) return;
    update({ holds: Math.max(0, state.holds - 1) });
  };
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const getSnapshot = () => state;
const getServerSnapshot = () => IDLE_AUTH_HANDOFF;

export function useAuthHandoffState(): AuthHandoffState {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

export function readAuthHandoffState(): AuthHandoffState {
  return state;
}

/**
 * Keeps the veil up while this loader is mounted during a handoff, so the
 * route loaders between sign-in and the app never show as a second loader.
 * A layout effect: the hold lands in the same commit as the route change,
 * before the veil checks whether it can lift.
 */
export function useAuthHandoffHold(holding = true) {
  useLayoutEffect(() => {
    if (!holding) return;
    return addAuthHandoffHold();
  }, [holding]);
}

/**
 * Lifts the veil (through its normal reveal) when this mounts: for screens
 * on a transit route that need the user, such as an error with a retry.
 */
export function useAuthHandoffRelease() {
  useLayoutEffect(() => {
    yieldAuthHandoff();
  }, []);
}

/** Matches `--imai-duration-exit`: the card recede and the veil's fade-in. */
export const AUTH_HANDOFF_EXIT_MS = 160;

let pendingHref: string | null = null;

/** Where a soft sign-in is navigating, until `/auth/signing-in` renders. */
export function readAuthHandoffPendingHref(): string | null {
  return state.active && !state.reachedSigningIn ? pendingHref : null;
}

/**
 * Starts the handoff and navigates to `/auth/signing-in` once the veil is
 * opaque. The card recedes and the veil comes up first, so the route swap
 * (and its loading boundary) only ever paints under it. The wait is one exit
 * beat, while the Convex token exchange that /auth/signing-in waits for is
 * already running, so it doesn't lengthen the sign-in.
 */
export function pushAuthHandoff(
  router: { push: (href: string) => void },
  href: string
): true {
  beginAuthHandoff();
  pendingHref = href;
  const generation = state.generation;
  window.setTimeout(() => {
    if (state.generation === generation && state.active) router.push(href);
  }, AUTH_HANDOFF_EXIT_MS);
  return true;
}
